import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { keyHandoversAPI, propertiesAPI, clientsAPI, signaturesAPI, keysInventoryAPI } from '../lib/api';
import { toast } from 'sonner';
import { SignaturePad } from '../components/SignaturePad';
import {
  Key, Plus, Search, Trash2, Download, Eye, PenLine,
  Building2, User, Calendar, ArrowRightLeft, CheckCircle2, PackageCheck
} from 'lucide-react';

const KEY_TYPE_LABELS = {
  porte_principale: "Porte principale", portail: "Portail",
  boite_aux_lettres: "Boîte aux lettres", garage: "Garage",
  cave: "Cave", local_technique: "Local technique",
  badge_acces: "Badge d'accès", telecommande: "Télécommande", autre: "Autre"
};

const CONDITION_LABELS = { bon: "Bon état", usage: "Usagé", mauvais: "Mauvais état" };
const CONDITION_COLORS = { bon: "bg-green-100 text-green-700", usage: "bg-amber-100 text-amber-700", mauvais: "bg-red-100 text-red-700" };

const KeyHandovers = () => {
  const [handovers, setHandovers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    property_id: '', tenant_id: '', handover_type: 'remise', handover_date: '', observations: ''
  });
  const [keyItems, setKeyItems] = useState([{ key_type: 'porte_principale', quantity: 1, description: '', condition: 'bon' }]);
  const [stockKeys, setStockKeys] = useState([]);  // cles disponibles dans le stock du bien
  const [selectedStockIds, setSelectedStockIds] = useState([]);
  const [loadingStock, setLoadingStock] = useState(false);

  // Detail modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedHandover, setSelectedHandover] = useState(null);

  // Sign modal
  const [showSignModal, setShowSignModal] = useState(false);
  const [signHandoverId, setSignHandoverId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [handoversRes, propsRes, tenantsRes] = await Promise.all([
        keyHandoversAPI.getAll(),
        propertiesAPI.getAll({ transaction_type: 'location' }),
        clientsAPI.getAll({ client_type: 'locataire' })
      ]);
      setHandovers(handoversRes.data);
      setProperties(propsRes.data);
      setTenants(tenantsRes.data);
    } catch {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const addKeyItem = () => {
    setKeyItems(prev => [...prev, { key_type: 'porte_principale', quantity: 1, description: '', condition: 'bon' }]);
  };

  const removeKeyItem = (index) => {
    setKeyItems(prev => prev.filter((_, i) => i !== index));
  };

  const updateKeyItem = (index, field, value) => {
    setKeyItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createForm.property_id || !createForm.tenant_id || !createForm.handover_date) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    if (keyItems.length === 0 && selectedStockIds.length === 0) {
      toast.error('Ajoutez au moins une cle (depuis le stock ou en saisie libre)');
      return;
    }
    try {
      await keyHandoversAPI.create({
        ...createForm,
        keys: keyItems,
        key_inventory_ids: selectedStockIds,
      });
      toast.success('Remise de clés enregistrée');
      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const resetForm = () => {
    setCreateForm({ property_id: '', tenant_id: '', handover_type: 'remise', handover_date: '', observations: '' });
    setKeyItems([{ key_type: 'porte_principale', quantity: 1, description: '', condition: 'bon' }]);
    setStockKeys([]);
    setSelectedStockIds([]);
  };

  // Charger les cles du stock quand le bien change (pour 'remise')
  useEffect(() => {
    if (!createForm.property_id) {
      setStockKeys([]);
      setSelectedStockIds([]);
      return;
    }
    let cancel = false;
    (async () => {
      setLoadingStock(true);
      try {
        const r = await keysInventoryAPI.getByProperty(createForm.property_id);
        if (cancel) return;
        // Pour 'remise' : on ne propose que les cles encore en stock
        // Pour 'restitution' : on propose les cles actuellement avec ce locataire
        const all = r.data || [];
        if (createForm.handover_type === 'remise') {
          setStockKeys(all.filter((k) => k.status === 'in_stock' || k.status === 'returned'));
        } else {
          setStockKeys(all.filter((k) => k.status === 'with_tenant'
            && (!createForm.tenant_id || k.current_tenant_id === createForm.tenant_id)));
        }
        setSelectedStockIds([]);
      } catch {
        setStockKeys([]);
      }
      setLoadingStock(false);
    })();
    return () => { cancel = true; };
  }, [createForm.property_id, createForm.handover_type, createForm.tenant_id]);

  const toggleStockKey = (id) => {
    setSelectedStockIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };
  const selectAllStock = () => setSelectedStockIds(stockKeys.map((k) => k.id));
  const clearAllStock = () => setSelectedStockIds([]);

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette remise de clés ?')) return;
    try {
      await keyHandoversAPI.delete(id);
      toast.success('Supprimé');
      fetchData();
      if (selectedHandover?.id === id) setShowDetailModal(false);
    } catch {
      toast.error('Erreur');
    }
  };

  const handleDownloadPdf = async (id, ref) => {
    try {
      const response = await keyHandoversAPI.downloadPdf(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `attestation_cles_${ref}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Attestation PDF téléchargée');
    } catch {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleSign = async (signatureData) => {
    if (!signHandoverId) return;
    try {
      await signaturesAPI.save({
        document_type: 'key_handover',
        document_id: signHandoverId,
        signature_data: signatureData
      });
      toast.success('Document signé');
      setShowSignModal(false);
      setSignHandoverId(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  // Get tenants for a selected property
  // If no tenants are assigned to the property, show all tenants
  const getPropertyTenants = (propertyId) => {
    if (!propertyId) return tenants;
    const propertyTenants = tenants.filter(t => t.assigned_property_id === propertyId);
    // If no tenants assigned to this property, return all tenants
    return propertyTenants.length > 0 ? propertyTenants : tenants;
  };

  const filtered = handovers.filter(h => {
    const matchSearch = !searchTerm || h.tenant_name?.toLowerCase().includes(searchTerm.toLowerCase()) || h.property_title?.toLowerCase().includes(searchTerm.toLowerCase()) || h.reference?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchType = filterType === 'all' || h.handover_type === filterType;
    return matchSearch && matchType;
  });

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="key-handovers-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Remise de Clés</h1>
          <p className="text-gray-500 mt-1">Gérez les remises et restitutions de clés</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="bg-[#1B3A4B] hover:bg-[#142D3A]" data-testid="create-handover-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouvelle Remise
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-semibold text-gray-900">{handovers.length}</p>
          <p className="text-xs text-gray-500">Total</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-semibold text-[#1B3A4B]">{handovers.filter(h => h.handover_type === 'remise').length}</p>
          <p className="text-xs text-gray-500">Remises</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-semibold text-blue-600">{handovers.filter(h => h.handover_type === 'restitution').length}</p>
          <p className="text-xs text-gray-500">Restitutions</p>
        </CardContent></Card>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Rechercher..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-handovers" />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-[180px]" data-testid="filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            <SelectItem value="remise">Remises</SelectItem>
            <SelectItem value="restitution">Restitutions</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* List */}
      <div className="space-y-3">
        {filtered.length === 0 ? (
          <Card><CardContent className="p-8 text-center text-gray-500">
            <Key className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Aucune remise de clés trouvée</p>
          </CardContent></Card>
        ) : filtered.map(h => (
          <Card key={h.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => { setSelectedHandover(h); setShowDetailModal(true); }} data-testid={`handover-card-${h.id}`}>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${h.handover_type === 'remise' ? 'bg-[#1B3A4B]/10' : 'bg-blue-100'}`}>
                    <Key className={`w-5 h-5 ${h.handover_type === 'remise' ? 'text-[#1B3A4B]' : 'text-blue-600'}`} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-xs text-gray-400">{h.reference}</span>
                      <Badge className={h.handover_type === 'remise' ? 'bg-[#1B3A4B]/10 text-[#1B3A4B]' : 'bg-blue-100 text-blue-700'}>
                        {h.handover_type === 'remise' ? 'Remise' : 'Restitution'}
                      </Badge>
                    </div>
                    <p className="font-medium text-gray-900 truncate">{h.tenant_name}</p>
                    <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-gray-500">
                      <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{h.property_title}</span>
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{h.handover_date}</span>
                      <span className="flex items-center gap-1"><Key className="w-3 h-3" />{h.total_keys} clé(s)</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                  <Button variant="ghost" size="icon" onClick={() => handleDownloadPdf(h.id, h.reference)} title="Attestation PDF" data-testid={`pdf-handover-${h.id}`}>
                    <Download className="w-4 h-4 text-[#1B3A4B]" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => { setSignHandoverId(h.id); setShowSignModal(true); }} title="Signer" data-testid={`sign-handover-${h.id}`}>
                    <PenLine className="w-4 h-4 text-[#1B3A4B]" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(h.id)} title="Supprimer" data-testid={`delete-handover-${h.id}`}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={(open) => { setShowCreateModal(open); if (!open) resetForm(); }}>
        <DialogContent className="sm:max-w-[650px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Key className="w-5 h-5 text-[#1B3A4B]" /> Nouvelle Remise de Clés</DialogTitle>
            <DialogDescription>Enregistrez une remise ou restitution de clés</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type *</Label>
                <Select value={createForm.handover_type} onValueChange={v => setCreateForm(f => ({ ...f, handover_type: v }))}>
                  <SelectTrigger data-testid="handover-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="remise">Remise de clés</SelectItem>
                    <SelectItem value="restitution">Restitution de clés</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date *</Label>
                <Input type="date" value={createForm.handover_date} onChange={e => setCreateForm(f => ({ ...f, handover_date: e.target.value }))} data-testid="handover-date" />
              </div>
            </div>
            <div>
              <Label>Bien immobilier *</Label>
              <Select value={createForm.property_id} onValueChange={v => setCreateForm(f => ({ ...f, property_id: v, tenant_id: '' }))}>
                <SelectTrigger data-testid="handover-property"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>{properties.map(p => <SelectItem key={p.id} value={p.id}>{p.title} - {p.district}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Locataire *</Label>
              <Select value={createForm.tenant_id} onValueChange={v => setCreateForm(f => ({ ...f, tenant_id: v }))}>
                <SelectTrigger data-testid="handover-tenant"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>
                  {getPropertyTenants(createForm.property_id).map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Stock keys selector (linked to property) */}
            {createForm.property_id && (
              <div className="border rounded-lg p-3 bg-amber-50/50 border-amber-200">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-semibold flex items-center gap-1">
                    <PackageCheck className="w-4 h-4 text-[#D4A745]" />
                    {createForm.handover_type === 'remise'
                      ? 'Selectionner depuis le stock du bien'
                      : 'Cles a restituer (actuellement avec le locataire)'}
                  </Label>
                  {stockKeys.length > 0 && (
                    <div className="flex gap-1">
                      <Button type="button" size="sm" variant="ghost" onClick={selectAllStock}
                              className="h-6 text-xs">Tout</Button>
                      <Button type="button" size="sm" variant="ghost" onClick={clearAllStock}
                              className="h-6 text-xs">Aucun</Button>
                    </div>
                  )}
                </div>
                {loadingStock ? (
                  <div className="text-xs text-gray-500">Chargement...</div>
                ) : stockKeys.length === 0 ? (
                  <div className="text-xs text-gray-500 italic">
                    {createForm.handover_type === 'remise'
                      ? 'Aucune cle disponible dans le stock pour ce bien. Vous pouvez en creer dans la fiche du bien (onglet "Stock de cles") ou saisir manuellement ci-dessous.'
                      : 'Aucune cle actuellement avec un locataire pour ce bien.'}
                  </div>
                ) : (
                  <div className="space-y-1 max-h-44 overflow-y-auto" data-testid="stock-keys-list">
                    {stockKeys.map((k) => (
                      <label key={k.id}
                             className="flex items-center gap-2 p-2 hover:bg-amber-100/40 rounded cursor-pointer text-sm"
                             data-testid={`stock-key-${k.id}`}>
                        <input type="checkbox"
                               checked={selectedStockIds.includes(k.id)}
                               onChange={() => toggleStockKey(k.id)}
                               className="w-4 h-4 accent-[#D4A745]" />
                        <span className="font-mono text-xs font-semibold">{k.reference}</span>
                        <span className="text-gray-700">{k.key_type_label}</span>
                        {k.label && <span className="text-gray-500 text-xs">- {k.label}</span>}
                        {k.unit_name && <span className="text-xs text-gray-500 ml-auto">{k.unit_name}</span>}
                      </label>
                    ))}
                  </div>
                )}
                {selectedStockIds.length > 0 && (
                  <div className="text-xs text-emerald-700 mt-2 font-medium">
                    {selectedStockIds.length} cle(s) selectionnee(s) -
                    {createForm.handover_type === 'remise'
                      ? ' passeront en "Avec locataire" automatiquement'
                      : ' passeront en "Retournee" automatiquement'}
                  </div>
                )}
              </div>
            )}

            {/* Keys Section */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold">Cles supplementaires (saisie libre)</Label>
                <Button type="button" size="sm" variant="outline" onClick={addKeyItem} data-testid="add-key-btn">
                  <Plus className="w-3 h-3 mr-1" /> Ajouter
                </Button>
              </div>
              <div className="space-y-2">
                {keyItems.map((item, idx) => (
                  <div key={idx} className="border border-gray-100 rounded-lg p-3 space-y-2" data-testid={`key-item-${idx}`}>
                    <div className="grid grid-cols-3 gap-2">
                      <Select value={item.key_type} onValueChange={v => updateKeyItem(idx, 'key_type', v)}>
                        <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(KEY_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Input type="number" min="1" value={item.quantity} onChange={e => updateKeyItem(idx, 'quantity', parseInt(e.target.value) || 1)} className="text-xs" placeholder="Qté" />
                      <Select value={item.condition} onValueChange={v => updateKeyItem(idx, 'condition', v)}>
                        <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="bon">Bon état</SelectItem>
                          <SelectItem value="usage">Usagé</SelectItem>
                          <SelectItem value="mauvais">Mauvais état</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-2">
                      <Input value={item.description} onChange={e => updateKeyItem(idx, 'description', e.target.value)} placeholder="Description (optionnel)" className="text-xs flex-1" />
                      {keyItems.length > 1 && (
                        <Button type="button" size="icon" variant="ghost" onClick={() => removeKeyItem(idx)} className="text-red-500 h-8 w-8">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label>Observations</Label>
              <Textarea value={createForm.observations} onChange={e => setCreateForm(f => ({ ...f, observations: e.target.value }))} placeholder="Observations..." data-testid="handover-observations" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowCreateModal(false); resetForm(); }}>Annuler</Button>
              <Button type="submit" className="bg-[#1B3A4B] hover:bg-[#142D3A]" data-testid="submit-handover">Enregistrer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          {selectedHandover && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Key className="w-5 h-5 text-[#1B3A4B]" />
                  {selectedHandover.handover_type === 'remise' ? 'Remise' : 'Restitution'} de Clés
                </DialogTitle>
                <DialogDescription>{selectedHandover.reference} - {selectedHandover.handover_date}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-gray-500">Locataire:</span><br /><span className="font-medium">{selectedHandover.tenant_name}</span></div>
                  <div><span className="text-gray-500">Bien:</span><br /><span className="font-medium">{selectedHandover.property_title}</span></div>
                  <div><span className="text-gray-500">Adresse:</span><br /><span className="font-medium">{selectedHandover.property_address}</span></div>
                  <div><span className="text-gray-500">Type:</span><br />
                    <Badge className={selectedHandover.handover_type === 'remise' ? 'bg-[#1B3A4B]/10 text-[#1B3A4B]' : 'bg-blue-100 text-blue-700'}>
                      {selectedHandover.handover_type === 'remise' ? 'Remise' : 'Restitution'}
                    </Badge>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Détail des clés ({selectedHandover.total_keys})</h3>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead><tr className="bg-gray-50 border-b">
                        <th className="py-2 px-3 text-left text-gray-500">Type</th>
                        <th className="py-2 px-3 text-center text-gray-500">Qté</th>
                        <th className="py-2 px-3 text-left text-gray-500">Description</th>
                        <th className="py-2 px-3 text-center text-gray-500">État</th>
                      </tr></thead>
                      <tbody>
                        {selectedHandover.keys?.map((k, idx) => (
                          <tr key={idx} className="border-b last:border-0">
                            <td className="py-2 px-3">{KEY_TYPE_LABELS[k.key_type] || k.key_type}</td>
                            <td className="py-2 px-3 text-center font-medium">{k.quantity}</td>
                            <td className="py-2 px-3 text-gray-600">{k.description || '-'}</td>
                            <td className="py-2 px-3 text-center">
                              <Badge className={CONDITION_COLORS[k.condition] || 'bg-gray-100'}>{CONDITION_LABELS[k.condition] || k.condition}</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {selectedHandover.observations && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Observations</h3>
                    <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">{selectedHandover.observations}</p>
                  </div>
                )}
              </div>
              <DialogFooter className="flex-wrap gap-2">
                <Button variant="outline" onClick={() => handleDownloadPdf(selectedHandover.id, selectedHandover.reference)} data-testid="detail-pdf-btn">
                  <Download className="w-4 h-4 mr-2" /> Attestation PDF
                </Button>
                <Button variant="outline" onClick={() => { setSignHandoverId(selectedHandover.id); setShowSignModal(true); setShowDetailModal(false); }} className="text-[#1B3A4B] border-[#1B3A4B]/30" data-testid="detail-sign-btn">
                  <PenLine className="w-4 h-4 mr-2" /> Signer
                </Button>
                <Button onClick={() => setShowDetailModal(false)}>Fermer</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Sign Modal */}
      <Dialog open={showSignModal} onOpenChange={(open) => { setShowSignModal(open); if (!open) setSignHandoverId(null); }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenLine className="w-5 h-5 text-[#1B3A4B]" />
              Signer l'Attestation
            </DialogTitle>
          </DialogHeader>
          <SignaturePad
            onSave={handleSign}
            onCancel={() => { setShowSignModal(false); setSignHandoverId(null); }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KeyHandovers;
